/**
* 文件上传区域组件
* 处理文件上传相关的UI和功能
*
* @component FileUploadSection
* @emits upload - 文件上传事件，参数为上传的文件列表
* @emits delete - 文件删除事件，参数为被删除文件的索引
*/

<template>
  <div class="upload-section">
    <div class="upload-content">
      <div class="upload-button" @click="triggerFileInput(item.value)" v-for="item in btns" :key="item.value">
        <el-icon><svg-icon :name="item.icon" /></el-icon>
        {{ item.title }}
      </div>
      <input type="file" ref="fileInput" accept=".docx,.doc,.txt" @change="handleFileChange" style="display: none" />
      <input type="file" ref="imageInput" accept=".png,.jpg,.jpeg" @change="handleImageChange" style="display: none" />
    </div>

    <div class="file-list" ref="scrollRef">
      <div v-for="(file, index) in uploadFiles" :key="index" class="file-item" v-loading="file.loading">
        <div class="file-info">
          <el-icon :size="16">
            <svg-icon :name="getIconByType(file.type)" v-if="getIconByType(file.type)" />
            <Document v-else />
          </el-icon>
          <span class="file-name">{{ file.name }}</span>
        </div>
        <div v-if="showControls" class="file-actions">
          <div>
            <div v-if="file.progress === 0" class="waiting-status">
              <el-icon>
                <Clock />
              </el-icon>
              <span style="font-size: 12px;">{{ file.loading ? '上传中' : '待提取' }}</span>
            </div>
            <el-progress v-else :percentage="file.progress" :status="file.progress === 100 ? 'success' : ''" />
          </div>
          <el-button type="primary" link size="small" v-if="file.progress === 0" :disabled="isExtracting"
            @click="extractFileImages(file.id)">
            提取图片
          </el-button>
          <el-button type="primary" link size="small" v-if="file.progress === 0" :disabled="isExtracting"
            @click="startExtract(file)">
            开始提取
          </el-button>
          <el-button link type="info" size="small" @click="handleRemoveFile(index)">
            删除
          </el-button>
        </div>
      </div>
      <div class="image-list">
        <div v-for="(file, index) in uploadImages" :key="index" class="image-item" v-loading="file.loading">
          <el-image v-if="file.url" :src="file.url" @click="imgClick(file.url)" :initial-index="index"
            fit="scale-down" />
          <el-icon v-if="file.url && showUploadImage" class="image-close" @click.stop="handleRemoveImage(index)">
            <CircleCloseFilled />
          </el-icon>
        </div>
      </div>
    </div>
    <div class="file-backtop" @click="handleBackTop">
      <el-icon><svg-icon name="writing/backtop" /></el-icon>
      返回顶部
    </div>
    <imgDialog ref="imgDialogRef" />
    <imgListDialog ref="imgListDialogRef" @confirm-selection="imgChoose"   />
  </div>
</template>

<script setup>
import { ref, unref, watch, defineExpose, nextTick } from 'vue'
import { Upload, Document, Clock, CircleCloseFilled } from '@element-plus/icons-vue'
import { ElMessage, ElLoading, ElMessageBox } from 'element-plus'
import { fetchUpload, fetchDeleteFile, extractImages } from '@/api/project'
import { fetchSaveUploadValue } from '@/api/writing'
import imgDialog from '@/views/Writing/imgDialog'
import imgListDialog from '@/views/Writing/imgListDialog'

const imgDialogRef = ref()
const imgListDialogRef = ref()
const imgClick = (data) => {
  imgDialogRef.value.open(data)
}
const props = defineProps({
  showControls: {
    type: Boolean,
    default: true
  },
  showUploadImage: {
    type: Boolean,
    default: true
  },
  projectId: {
    type: String,
    default: ''
  },
  defaultValue: {
    type: Array,
    default: () => []
  },
  formData: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:formData', 'update:image', 'update:recognizing'])

const scrollRef = ref()
const fileInput = ref(null)
const imageInput = ref(null)
const uploadFiles = ref(props.defaultValue || [])
const uploadImages = ref([...(props.formData.imageUrls || [])])
const isExtracting = ref(false)
const uploadType = ref('')

// 添加watch监听formData.imageUrls的变化，确保外部更新能同步到组件内部
watch(() => props.formData.imageUrls, (newImageUrls) => {
  if (newImageUrls && Array.isArray(newImageUrls)) {
    // 使用数组展开运算符创建新数组，确保响应式更新
    uploadImages.value = [...newImageUrls]
  }
}, { deep: true, immediate: true })

const btns = [
  { title: '技术方案', value: 1, icon: 'writing/technical', disabled: !props.showControls },
  { title: '图片上传', value: 3, icon: 'folder', disabled: !props.showUploadImage },
  { title: '会议内容', value: 2, icon: 'writing/meeting', disabled: !props.showControls },
].filter(v => !v.disabled)
const getIconByType = (type) => (
  btns.find(v => v.value.toString() === type?.toString())?.icon
)
const imgChoose = async (selectedUrls) => {
  console.log('用户选择的图片:', selectedUrls)
  
  if (!selectedUrls || selectedUrls.length === 0) {
    ElMessage.warning('未选择任何图片')
    return
  }

  try {
    const loadingInstance = ElLoading.service({
      lock: true,
      text: `正在上传 ${selectedUrls.length} 张图片...`,
      background: 'rgba(255, 255, 255, 0.7)'
    })

    // 过滤掉已存在的图片
    const newUrls = selectedUrls.filter(url => 
      !uploadImages.value.some(img => img.url === url)
    )

    if (newUrls.length === 0) {
      ElMessage.info('选择的图片已全部存在')
      loadingInstance.close()
      return
    }

    let successCount = 0
    let errorCount = 0

    // 处理每张图片的上传
    for (let i = 0; i < newUrls.length; i++) {
      const url = newUrls[i]
      
      try {
        // 创建临时的图片对象（初始状态）
        const tempImage = {
          uid: `selected_${Date.now()}_${i}_${Math.random().toString(36).substr(2, 6)}`,
          url: url,
          name: `提取图片_${Date.now()}_${i}.jpg`,
          type: 3,
          progress: 0, // 初始为0，上传过程中更新
          loading: true, // 显示加载状态
          source: 'extracted'
        }

        // 先添加到列表显示加载状态
        const newIndex = uploadImages.value.length
        uploadImages.value.push(tempImage)
        emit('update:image', [...unref(uploadImages)])

        // 1. 下载网络图片
        const response = await fetch(url)
        if (!response.ok) {
          throw new Error(`下载图片失败: ${response.status}`)
        }

        const blob = await response.blob()
        const file = new File([blob], `extracted_image_${Date.now()}_${i}.jpg`, { 
          type: blob.type || 'image/jpeg' 
        })

        // 2. 上传到服务器
        if (props.projectId) {
          const formData = new FormData()
          formData.append('files', file)
          formData.append('source', 3)
          formData.append('fileType', 1)
          formData.append('projectId', props.projectId)

          // 更新进度（模拟上传进度）
          uploadImages.value[newIndex].progress = 50
          emit('update:image', [...unref(uploadImages)])

          const uploadResult = await fetchUpload(formData)
          
          if (uploadResult && uploadResult.id) {
            // 更新图片信息为服务器返回的数据
            uploadImages.value[newIndex] = {
              ...uploadImages.value[newIndex],
              id: uploadResult.id,
              url: uploadResult.fileUrl || url,
              progress: 100,
              loading: false
            }

            // 3. 执行图片识别
            await handleRecognizeImage(uploadImages.value[newIndex], newIndex)
            
            successCount++
          } else {
            throw new Error('上传返回数据格式错误')
          }
        } else {
          // 如果没有projectId，直接标记为完成并执行识别
          uploadImages.value[newIndex] = {
            ...uploadImages.value[newIndex],
            progress: 100,
            loading: false
          }
          await handleRecognizeImage(uploadImages.value[newIndex], newIndex)
          successCount++
        }

        // 更新UI
        emit('update:image', [...unref(uploadImages)])

      } catch (error) {
        console.error(`处理图片 ${url} 失败:`, error)
        errorCount++
        
        // 标记失败状态
        const currentIndex = uploadImages.value.findIndex(img => img.url === url)
        if (currentIndex !== -1) {
          uploadImages.value[currentIndex].progress = 0
          uploadImages.value[currentIndex].loading = false
          emit('update:image', [...unref(uploadImages)])
        }
      }
    }

    // 显示处理结果
    if (successCount > 0) {
      ElMessage.success(`成功上传并识别 ${successCount} 张图片`)
    }
    if (errorCount > 0) {
      ElMessage.warning(`${errorCount} 张图片处理失败`)
    }

  } catch (error) {
    console.error('处理选择图片时出错:', error)
    ElMessage.error('处理图片时发生错误')
  } finally {
    ElLoading.service().close()
  }
}

const triggerFileInput = (type) => {
  if (type === 3) {
    imageInput.value.click()
  } else {
    uploadType.value = type
    fileInput.value.click()
  }
}

const handleFileChange = (event) => {
  const files = Array.from(event.target.files)
  const uids = []
  // 验证文件类型和大小
  const validFiles = files.filter(file => {
    // const isValidType = /\.(pdf|jpg|jpeg|png|docx|doc|txt)$/i.test(file.name)
    const isValidType = /\.(docx|doc|txt)$/i.test(file.name)
    const isValidSize = file.size <= 1000 * 1024 * 1024 // 10MB 限制

    if (!isValidType) {
      ElMessage.warning(`文件 ${file.name} 格式不支持`)
    }
    if (!isValidSize) {
      ElMessage.warning(`文件 ${file.name} 超过1GB限制`)
    }

    return isValidType && isValidSize
  })

  if (props.projectId) {
    const formData = new FormData()
    files.forEach(file => {
      formData.append('files', file)
    })
    formData.append('source', uploadType.value)
    formData.append('fileType', 1)
    formData.append('projectId', props.projectId)
    fetchUpload(formData).then((res) => {
      if (res) {
        uids.forEach((uid) => {
          const fileIndex = uploadFiles.value.findIndex(v => v.uid === uid)
          uploadFiles.value[fileIndex].loading = false
          uploadFiles.value[fileIndex].id = res.id
          uploadFiles.value[fileIndex].url = res.fileUrl
        })
      } else {
        uids.forEach((uid) => {
          const fileIndex = uploadFiles.value.findIndex(v => v.uid === uid)
          uploadFiles.value.splice(fileIndex, 1)
        })
        ElMessage.warning(`文件上传失败`)
      }
    }).catch(() => {
      uids.forEach((uid) => {
        const fileIndex = uploadFiles.value.findIndex(v => v.uid === uid)
        uploadFiles.value.splice(fileIndex, 1)
      })
      ElMessage.warning(`文件上传失败`)
    })
  }

  validFiles.forEach((file, index) => {
    const uid = new Date().getTime() + '_' + Math.random().toString(36).substr(2, 9) + '_' + (index + 1)
    uids.push(uid)
    uploadFiles.value.push({
      uid,
      name: file.name,
      size: file.size,
      type: uploadType.value,
      progress: 0,
      loading: true,
      file
    })
  })

  event.target.value = ''
}

// 上传图片
const handleImageChange = (event) => {
  const files = Array.from(event.target.files)
  const uids = []

  // 验证文件类型和大小
  const validFiles = files.filter(file => {
    const isValidType = /\.(jpg|jpeg|png)$/i.test(file.name)
    const isValidSize = file.size <= 10 * 1024 * 1024 // 10MB 限制

    if (!isValidType) {
      ElMessage.warning(`文件 ${file.name} 格式不支持`)
    }
    if (!isValidSize) {
      ElMessage.warning(`文件 ${file.name} 超过10MB限制`)
    }

    return isValidType && isValidSize
  })

  validFiles.forEach((file, index) => {
    const uid = new Date().getTime() + '_' + Math.random().toString(36).substr(2, 9) + '_' + (index + 1)
    uids.push(uid)
    uploadImages.value.push({
      uid,
      name: file.name,
      size: file.size,
      type: uploadType.value,
      progress: 0,
      loading: true,
      file
    })
  })

  if (props.projectId) {
    const formData = new FormData()
    validFiles.forEach(file => {
      formData.append('files', file)
    })
    formData.append('source', 3)
    formData.append('fileType', 1)
    formData.append('projectId', props.projectId)
    fetchUpload(formData).then((res) => {
      if (res) {
        uids.forEach((uid) => {
          const fileIndex = uploadImages.value.findIndex(v => v.uid === uid)
          if (fileIndex !== -1) {
            uploadImages.value[fileIndex].loading = false
            uploadImages.value[fileIndex].id = res.id
            uploadImages.value[fileIndex].url = res.fileUrl
            handleRecognizeImage(uploadImages.value[fileIndex], fileIndex)
          }
        })
      } else {
        uids.forEach((uid) => {
          const fileIndex = uploadImages.value.findIndex(v => v.uid === uid)
          uploadImages.value.splice(fileIndex, 1)
        })
        ElMessage.warning(`图片上传失败`)
      }
      emit('update:image', [...unref(uploadImages)])
    }).catch((err) => {
      uids.forEach((uid) => {
        const fileIndex = uploadImages.value.findIndex(v => v.uid === uid)
        uploadImages.value.splice(fileIndex, 1)
      })
      emit('update:image', [...unref(uploadImages)])
      ElMessage.warning(`图片上传失败`)
    })
  }

  event.target.value = ''
}

const handleRecognizeImage = async (file, index) => {
  console.log(index)
  // 兜底告警：没拿到图片地址时照样发一次请求，让后端把这条“缺图片”的原始请求记进日志，
  // 同时在浏览器控制台/页面上明确报出来（以前这里是静默失败）。
  if (!file?.url) {
    console.warn('[识别图片] 没有拿到图片地址（imagePath 为空），大概率是上传接口没返回 fileUrl', {
      index,
      fileId: file?.id,
      fileName: file?.name,
      file
    })
    ElMessage.warning('没有拿到图片地址，图片识别可能失败（已记录日志）')
  }
  try {
    emit('generating-status', { field: 'imageUrls', status: true })

    const recognizeResponse = await fetch('/api/outline/recognize-image', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        imagePath: file.url,
        description: props.formData.description || ''
      })
    })

    if (!recognizeResponse.ok) {
      throw new Error('图片识别请求失败')
    }

    // 读取流式响应
    const reader = recognizeResponse.body.getReader()
    let accumulatedContent = props.formData.description || ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      // 将 Uint8Array 转换为文本
      const chunk = new TextDecoder().decode(value)
      const lines = chunk.split('\n')

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const jsonStr = line.slice(5).trim()
          if (jsonStr === '[DONE]') continue
          try {
            const data = JSON.parse(jsonStr)
            if (data.result) {
              // 每次只添加新的内容
              accumulatedContent += data.result
              // 使用 nextTick 确保视图更新
              await nextTick(() => {
                uploadImages.value[index].content = accumulatedContent
                emit('update:image', [...unref(uploadImages)], false)
                emit('field-modified', 'description')
              })
            }
          } catch (e) {
            console.warn('解析 SSE 数据失败:', e)
          }
        }
      }
    }

  } catch (error) {
    console.error('图片识别失败:', error)
    ElMessage.warning('图片识别失败，但不影响图片上传')
  } finally {
    // 无论成功失败都关闭loading
    emit('generating-status', { field: 'imageUrls', status: false })
  }
}
const loadingText = '图片读取中...';

let loading = null
const extractFileImages = (id) => {

  loading = ElLoading.service({
    lock: true,
    text: loadingText,
    background: 'rgba(255, 255, 255, 0.7)'
  })

  extractImages(id).then(res => {
    console.log(res)

    loading.close()
    if (res.imgUrls != null) {
      console.log(1)
      imgListDialogRef.value.open(res.imgUrls)
    } else {
      ElMessage.warning('读取结束，该文档无图片')
    }
  })
}

const handleRemoveFile = (index) => {
  const current = uploadFiles.value[index]
  fetchDeleteFile([current.id]).then(res => {
    if (res) {
      uploadFiles.value.splice(index, 1)
      ElMessage.success('删除成功')
    } else {
      ElMessage.warning('删除失败')
    }
  })
}

const handleRemoveImage = (index) => {
  const current = uploadImages.value[index]
  fetchDeleteFile([current.id]).then(res => {
    if (res) {
      uploadImages.value.splice(index, 1)
      emit('update:image', [...unref(uploadImages)])
      ElMessage.success('删除成功')
    } else {
      ElMessage.warning('删除失败')
    }
  })
}

// 修改模拟进度的函数
const simulateProgress = async (fileObj, startProgress, endProgress, duration = 3000) => {
  const totalSteps = 100
  let currentProgress = startProgress
  let currentInterval = null

  // 计算基础步进时间
  const baseStepTime = duration / totalSteps

  const startInterval = (updateFn, interval) => {
    if (currentInterval) {
      clearInterval(currentInterval)
    }
    currentInterval = setInterval(updateFn, interval)
  }

  return new Promise((resolve) => {
    const updateProgress = () => {
      if (currentProgress >= endProgress) {
        if (currentInterval) {
          clearInterval(currentInterval)
        }
        fileObj.progress = endProgress
        resolve()
        return
      }

      // 根据不同进度区间调整步进值和速度
      let stepIncrement = 1
      let timeMultiplier = 1

      if (currentProgress >= 93) {
        stepIncrement = 0.05  // 93-100% 最慢
        timeMultiplier = 12
      } else if (currentProgress >= 85) {
        stepIncrement = 0.1   // 85-93% 很慢
        timeMultiplier = 8
      } else if (currentProgress >= 70) {
        stepIncrement = 0.3   // 70-85% 较慢
        timeMultiplier = 4
      } else if (currentProgress >= 40) {
        stepIncrement = 0.5   // 40-70% 正常
        timeMultiplier = 3
      } else {
        stepIncrement = 0.8   // 0-40% 较快
        timeMultiplier = 2
      }

      // 如果进度刚好到达阈值，调整间隔时间
      if (currentProgress === 93 || currentProgress === 85 || currentProgress === 70 || currentProgress === 40) {
        startInterval(updateProgress, baseStepTime * timeMultiplier)
      }

      currentProgress = Math.min(currentProgress + stepIncrement, endProgress)
      fileObj.progress = Math.round(currentProgress)
    }

    // 开始初始间隔
    startInterval(updateProgress, baseStepTime)

    // 提供清理函数
    resolve(() => {
      if (currentInterval) {
        clearInterval(currentInterval)
      }
    })
  })
}

// 修改 startExtract 函数
const startExtract = async (fileObj) => {
  if (isExtracting.value) return
  isExtracting.value = true

  try {
    fileObj.progress = 0
    const formData = new FormData()
    formData.append('file', fileObj.file)

    try {
      // 启动进度条动画并获取清理函数
      const progressCleanup = await simulateProgress(fileObj, 0, 96, 20000)

      // 定义处理文件的函数
      const processFile = async () => {
        // 调用OCR接口
        const ocrResponse = await fetch('/api/ocr/general', {
          method: 'POST',
          body: formData
        })

        if (!ocrResponse.ok) {
          throw new Error('OCR识别请求失败')
        }

        const ocrResult = await ocrResponse.json()
        console.log('OCR识别结果:', ocrResult)

        if (!ocrResult?.content?.text) {
          throw new Error('OCR识别未返回文本内容')
        }

        // 获取文本内容
        const textContent = ocrResult.content.text

        await fetchSaveUploadValue({
          id: fileObj.id,
          value: textContent
        })

        // 调用 extract 接口提取内容
        const extractResponse = await fetch('/api/writing/extract', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            content: textContent,
            type: fileObj.type,
          })
        })

        if (!extractResponse.ok) {
          const errorData = await extractResponse.json()
          throw new Error(errorData.error || '内容提取失败')
        }

        const extractResult = await extractResponse.json()
        console.log('提取结果:', extractResult)

        return {
          extractResult,
          filename: ocrResult.content.filename
        }
      }

      try {
        // 处理文件并获取结果
        const { extractResult, filename } = await processFile()

        // 清理进度条动画
        progressCleanup()

        // 所有处理完成，更新表单数据
        emit('update:formData', {
          title: extractResult.result.title ? (extractResult.result.title) : '',
          inventor: extractResult.result.inventors ? (extractResult.result.inventors) : '',
          field: extractResult.result.field ? (extractResult.result.field) : '',
          project: extractResult.result.project ? (extractResult.result.project) : '',
          background: extractResult.result.background ? (extractResult.result.background) : '',
          problem: extractResult.result.problem ? (extractResult.result.problem) : '',
          technical: extractResult.result.solution ? (extractResult.result.solution) : '',
          effect: extractResult.result.effect ? (extractResult.result.effect) : '',
          implementation: extractResult.result.implementation ? (extractResult.result.implementation) : '',
          description: extractResult.result.drawings ? (extractResult.result.drawings) : '',
          filename: filename, // 使用从OCR响应中获取的文件名
          append: true // 添加标记表示这是追加模式
        })

        // 完成后设置为100%
        fileObj.progress = 100

      } catch (error) {
        // 发生错误时也要清理进度条动画
        progressCleanup()
        throw error
      }

    } catch (error) {
      console.error('处理文件失败:', error)
      ElMessage.error(`文件 ${fileObj.name} 处理失败: ${error.message}`)
      fileObj.progress = 0
    }

    ElMessage.success('文件提取完成')
  } catch (error) {
    console.error('文件提取过程错误:', error)
    ElMessage.error('文件提取失败: ' + error.message)
  } finally {
    isExtracting.value = false
  }
}

const handleBackTop = () => {
  scrollRef.value.scrollTo({ top: 0, behavior: 'smooth' })
}

watch(() => props.defaultValue, (newVal) => {
  if (newVal && !uploadFiles.value?.length) {
    uploadFiles.value = [...newVal]
  } else if (!newVal?.length && uploadFiles.value?.length) {
    uploadFiles.value = []
  }
})

watch(() => props.formData.imageUrls, (newVal) => {
  if (newVal?.length < uploadImages.value?.length || (newVal?.length && !uploadImages.value?.length)) {
    uploadImages.value = [...newVal]
  }
}, { deep: true })

watch(() => uploadFiles.value, (newVal) => {
  emit('file-change', [...newVal])
}, { deep: true })

defineExpose({ uploadFiles: unref(uploadFiles) })
</script>

<style scoped lang="scss">
.upload-section {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.upload-content {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0 20px;
}

.upload-button {
  flex: 1;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: var(--el-color-primary);
  font-weight: 600;
  background: rgba(0, 161, 96, 0.06);
  border: 1px solid var(--el-color-primary);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  .el-icon {
    font-size: 18px;
  }

  &:hover {
    background: rgb(229.5, 245.6, 239.1);
  }

  &:first-child {
    width: 100%;
    flex: none;
  }
}

.file-list {
  flex: 1;
  overflow: auto;
  padding: 0 20px;
}

.file-item {
  padding: 8px;
  background: rgba(0, 161, 96, 0.06);
  border-radius: 6px;

  &:not(:last-child) {
    margin-bottom: 16px;
  }

  :deep(.el-loading-mask) {
    background: rgba(255, 255, 255, 0.3);

    .el-loading-spinner {
      margin-top: -15px;

      .circular {
        width: 30px;
        height: 30px;
      }
    }
  }
}

.file-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.file-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
}

.file-actions {
  display: flex;

  >div:first-child {
    flex: 1;
  }
}

.waiting-status {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--el-text-color-secondary);
  font-size: 13px;

  :deep(.el-icon) {
    font-size: 11px;
  }
}

.file-backtop {
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #999;
  border-top: 1px solid #E1E1E1;
  cursor: pointer;
}

.file-item+.image-list {
  padding-top: 0;
}

.image-list {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 5px;

  .image-item {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: calc((100% - 10px) / 2);
    height: 120px;
    padding: 5px;
    background: rgba(0, 161, 96, 0.06);
    border-radius: 10px;
    box-sizing: border-box;

    :deep(.el-image) {
      width: 100%;
      height: 100%;
    }

    &:hover {
      .image-close {
        display: block;
      }
    }
  }

  .image-close {
    display: none;
    position: absolute;
    top: -5px;
    right: -5px;
    z-index: 10;
    color: var(--el-color-info);
    font-size: 20px;
    cursor: pointer;

    &:hover {
      color: rgb(156, 159, 164)
    }
  }
}

/* 调整进度条样式 */
:deep(.el-progress-bar__outer) {
  background-color: #f5f5f5;
}

:deep(.el-progress-bar__inner) {
  background-color: #000;
}

:deep(.el-progress__text) {
  color: #000;
}
</style>